import React, { useEffect, useState } from "react";
import { ArrowRight, Star, Check } from "lucide-react";
import AxosShell from "./AxosShell";
import { Block, useAnno } from "./Annotate";
import { EMAIL_PINS } from "./tests";
import { A, SERIF, SANS, MONO, ADS, SHOTS, QUIZ_URL } from "./brand";

const STAGES = [
  { key: "welcome1", label: "Welcome 1", from: "Axos <hello@ibdassist.com>", subject: "Start here, not with six bottles", pre: "Three questions, then your match." },
  { key: "welcome2", label: "Welcome 2 reminder", from: "Axos <hello@ibdassist.com>", subject: "Your match is still saved", pre: "Two minutes to pick it up where you left it." },
  { key: "abandon", label: "Quiz abandonment", from: "Axos <hello@ibdassist.com>", subject: "You stopped at question two", pre: "One question left. We kept your answers." },
  { key: "review", label: "Post purchase", from: "Axos <hello@ibdassist.com>", subject: "Week four. How are the afternoons?", pre: "This is usually when people notice something." },
];

const STAGE_OF_PIN = {
  "em-subject": 0,
  "em-hero": 0,
  "em-pillars": 0,
  "em-products": 0,
  "em-offer": 0,
  "em-reminder": 1,
  "em-abandon": 2,
  "em-review": 3,
};

export default function AxosEmails() {
  return (
    <AxosShell pins={EMAIL_PINS} kicker="Axos · lifecycle emails">
      <EmailBody />
    </AxosShell>
  );
}

function EmailBody() {
  const anno = useAnno();
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (anno?.active && STAGE_OF_PIN[anno.active] !== undefined) setStage(STAGE_OF_PIN[anno.active]);
  }, [anno?.active]);

  const s = STAGES[stage];

  return (
    <div className="px-4 pb-16 pt-8 sm:px-8" style={{ background: "#0B0D11", minHeight: "100vh" }}>
      {/* stage switcher, the harness not the email */}
      <div className="mx-auto mb-6 flex max-w-[720px] flex-wrap items-center gap-2">
        {STAGES.map((x, i) => (
          <button
            key={x.key}
            type="button"
            data-testid={`email-stage-${x.key}`}
            aria-pressed={stage === i}
            onClick={() => setStage(i)}
            className="rounded-full px-3.5 py-1.5 text-[11.5px]"
            style={{
              fontFamily: SANS,
              color: stage === i ? "#0B0D11" : "#9AA6B6",
              background: stage === i ? "#E7ECF3" : "transparent",
              border: `1px solid ${stage === i ? "#E7ECF3" : "#242C38"}`,
            }}
          >
            {x.label}
          </button>
        ))}
      </div>

      {/* inbox line */}
      <Block id="em-subject" className="mx-auto mb-4 max-w-[720px] rounded-xl border p-4" style={{ borderColor: "#232A35", background: "#12161D" }}>
        <div className="text-[10px] uppercase tracking-[0.16em]" style={{ fontFamily: MONO, color: "#5F6B7A" }}>
          {s.from}
        </div>
        <div className="mt-1.5 text-[15px]" style={{ fontFamily: SANS, color: "#F2F5F9" }}>
          {s.subject}
        </div>
        <div className="mt-1 text-[12.5px]" style={{ fontFamily: SANS, color: "#7E8B9E" }}>
          {s.pre}
        </div>
      </Block>

      {/* the email itself, 600 wide like the real thing */}
      <div
        className="mx-auto w-full max-w-[600px] overflow-hidden"
        style={{ background: A.white, color: A.body, fontFamily: SANS, borderRadius: 10, boxShadow: "0 30px 70px rgba(0,0,0,0.4)" }}
      >
        <div className="px-6 py-5 text-center" style={{ borderBottom: `1px solid ${A.line}` }}>
          <div className="text-[20px]" style={{ fontFamily: SERIF, color: A.navy, fontWeight: 600 }}>
            axos
          </div>
          <div className="mt-1 text-[8.5px] tracking-[0.18em]" style={{ color: A.soft }}>
            SUPPORT THE CONNECTION.
          </div>
        </div>

        {stage === 0 && <Welcome1 />}
        {stage === 1 && <Welcome2 />}
        {stage === 2 && <Abandon />}
        {stage === 3 && <ReviewMail />}

        <div className="px-6 py-7 text-center text-[11px] leading-relaxed" style={{ background: A.navyDeep, color: "#9FB0C4" }}>
          <div className="text-[13px]" style={{ fontFamily: SERIF, color: A.white }}>
            Axos
          </div>
          <div className="mt-2">Food supplements are not a substitute for a varied diet or prescribed medication.</div>
          <div className="mt-2">quiz.ibdassist.com · hello@ibdassist.com</div>
          <div className="mt-3" style={{ color: "#7C8AA1" }}>
            Unsubscribe · Manage preferences
          </div>
        </div>
      </div>
    </div>
  );
}

function Btn({ children, testid }) {
  return (
    <a
      href={QUIZ_URL}
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-[13px] font-medium"
      style={{ background: A.blush, color: A.white, letterSpacing: "0.02em" }}
    >
      {children}
      <ArrowRight size={15} />
    </a>
  );
}

function Welcome1() {
  return (
    <>
      <Block id="em-hero">
        <img src={ADS.guided} alt="A more guided way to start" className="block w-full" />
        <div className="px-6 py-7 text-center">
          <h1 style={{ fontFamily: SERIF, color: A.navy, fontSize: 26, fontWeight: 500, lineHeight: 1.2 }}>
            A more guided way to start
          </h1>
          <p className="mx-auto mt-3 max-w-[420px] text-[13.5px] leading-relaxed" style={{ color: A.body }}>
            Most people arrive here with a shelf of bottles and no plan. Answer three questions and we will tell you which
            Axos routine fits your thyroid, your gut and the medication you already take.
          </p>
          <div className="mt-5">
            <Btn testid="email-hero-cta">Take the quiz</Btn>
          </div>
        </div>
      </Block>

      <Block id="em-pillars" className="px-6 pb-2">
        <div className="mb-4 text-center text-[9.5px] uppercase tracking-[0.18em]" style={{ fontFamily: MONO, color: A.teal }}>
          What we do differently
        </div>
        {[
          ["01", "Quiz led", "We ask before we recommend"],
          ["02", "Thyroid focused", "Selenium, zinc, vitamin D3 with K2, methylated B12"],
          ["03", "Doses on the label", "No proprietary blends, no filler list"],
        ].map(([n, t, d]) => (
          <div key={n} className="mb-2.5 flex items-start gap-3.5 rounded-xl p-4" style={{ background: A.cream, border: `1px solid ${A.line}` }}>
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px]" style={{ background: A.navy, color: A.white, fontFamily: MONO }}>
              {n}
            </span>
            <div>
              <div className="text-[13.5px] font-semibold" style={{ color: A.navy }}>
                {t}
              </div>
              <div className="mt-0.5 text-[12.5px]" style={{ color: A.soft }}>
                {d}
              </div>
            </div>
          </div>
        ))}
      </Block>

      <Block id="em-products" className="px-6 py-6">
        <div className="mb-4 text-center text-[9.5px] uppercase tracking-[0.18em]" style={{ fontFamily: MONO, color: A.blushDeep }}>
          Where most people start
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            [SHOTS.hashi, "Hashi-aid", "Daily thyroid support"],
            [SHOTS.gut, "Gut support", "For the absorption side"],
            [SHOTS.pair, "The duo", "Both, on subscription"],
          ].map(([img, t, d]) => (
            <div key={t} className="rounded-xl p-3 text-center" style={{ background: A.cream, border: `1px solid ${A.line}` }}>
              <img src={img} alt={t} className="mx-auto h-[92px] object-contain" />
              <div className="mt-2 text-[12.5px] font-semibold" style={{ color: A.navy }}>
                {t}
              </div>
              <div className="mt-0.5 text-[11.5px]" style={{ color: A.soft }}>
                {d}
              </div>
              <div className="mt-2.5 text-[11px] font-medium" style={{ color: A.blushDeep }}>
                See my match
              </div>
            </div>
          ))}
        </div>
      </Block>

      <Block id="em-offer" className="px-6 pb-8">
        <div className="rounded-2xl px-6 py-7 text-center" style={{ background: A.navy }}>
          <div style={{ fontFamily: SERIF, color: A.white, fontSize: 21, fontWeight: 500 }}>
            Your routine, delivered monthly
          </div>
          <div className="mt-2 text-[12.5px]" style={{ color: "#C3CEDC" }}>
            15% off every order, cancel or pause any time
          </div>
          <div className="mt-4">
            <Btn testid="email-offer-cta">Take the quiz</Btn>
          </div>
        </div>
      </Block>
    </>
  );
}

function Welcome2() {
  return (
    <Block id="em-reminder" className="px-6 py-7">
      <div className="text-center">
        <div className="text-[9.5px] uppercase tracking-[0.18em]" style={{ fontFamily: MONO, color: A.teal }}>
          Still saved
        </div>
        <h1 className="mt-2" style={{ fontFamily: SERIF, color: A.navy, fontSize: 26, fontWeight: 500, lineHeight: 1.2 }}>
          Your match is still here
        </h1>
        <p className="mx-auto mt-3 max-w-[400px] text-[13.5px] leading-relaxed" style={{ color: A.body }}>
          You answered two of three questions. We kept them, so picking it up takes about twenty seconds.
        </p>
      </div>
      <div className="mt-6 grid items-center gap-4 sm:grid-cols-[0.8fr_1.2fr]">
        <img src={SHOTS.hashi} alt="Hashi-aid" className="mx-auto h-[150px] object-contain" />
        <div>
          <div className="text-[13.5px] font-semibold" style={{ color: A.navy }}>
            Probable match: Hashi-aid
          </div>
          <ul className="mt-2.5 grid gap-2 text-[12.5px]" style={{ color: A.body }}>
            {["One capsule a day", "Four hours from your medication", "£33.15 on subscription"].map((l) => (
              <li key={l} className="flex gap-2">
                <Check size={14} color={A.teal} className="mt-[2px] shrink-0" />
                {l}
              </li>
            ))}
          </ul>
          <div className="mt-4">
            <Btn testid="email-reminder-cta">Finish the quiz</Btn>
          </div>
        </div>
      </div>
      <div className="mt-7 rounded-xl p-5" style={{ background: A.cream, border: `1px solid ${A.line}` }}>
        <div className="mb-2 flex items-center gap-1">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} size={12} fill={A.blush} color={A.blush} />
          ))}
        </div>
        <blockquote className="text-[13.5px] leading-relaxed" style={{ fontFamily: SERIF, color: A.ink }}>
          “Day 28 and I am steadier through the afternoon. That is the part I gave up on ever fixing.”
        </blockquote>
        <div className="mt-2 text-[11.5px]" style={{ color: A.soft }}>
          Priya R. · diagnosed 2023
        </div>
      </div>
    </Block>
  );
}

function Abandon() {
  return (
    <Block id="em-abandon" className="px-6 py-8">
      <div className="text-center">
        <h1 style={{ fontFamily: SERIF, color: A.navy, fontSize: 26, fontWeight: 500, lineHeight: 1.2 }}>
          One question left
        </h1>
        <p className="mx-auto mt-3 max-w-[380px] text-[13.5px] leading-relaxed" style={{ color: A.body }}>
          You stopped at question two, which is usually the medication one. Nothing you answer there is shared with anyone,
          and it is the answer that changes the recommendation most.
        </p>
        <img src={ADS.simplified} alt="Your supplement routine, simplified" className="mt-6 w-full rounded-[14px]" style={{ border: `1px solid ${A.line}` }} />
        <div className="mt-6">
          <Btn testid="email-abandon-cta">Pick up where I left off</Btn>
        </div>
        <div className="mt-3 text-[11.5px]" style={{ color: A.soft }}>
          Takes about twenty seconds
        </div>
      </div>
    </Block>
  );
}

function ReviewMail() {
  return (
    <Block id="em-review" className="px-6 py-8">
      <div className="text-center">
        <div className="text-[9.5px] uppercase tracking-[0.18em]" style={{ fontFamily: MONO, color: A.teal }}>
          Day 28
        </div>
        <h1 className="mt-2" style={{ fontFamily: SERIF, color: A.navy, fontSize: 26, fontWeight: 500, lineHeight: 1.2 }}>
          How are the afternoons?
        </h1>
        <p className="mx-auto mt-3 max-w-[400px] text-[13.5px] leading-relaxed" style={{ color: A.body }}>
          Four weeks in is when most people notice something, and it is almost always energy through the afternoon rather
          than anything dramatic. Tell us what changed, in your own words.
        </p>
        <div className="mt-5 flex items-center justify-center gap-1.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className="grid h-9 w-9 place-items-center rounded-full" style={{ border: `1px solid ${A.line}`, background: A.cream }}>
              <Star size={15} color={A.blush} />
            </span>
          ))}
        </div>
        <div className="mt-6">
          <Btn testid="email-review-cta">Leave a review</Btn>
        </div>
        <div className="mt-7 rounded-xl p-5 text-left" style={{ background: A.cream, border: `1px solid ${A.line}` }}>
          <div className="text-[12.5px] font-semibold" style={{ color: A.navy }}>
            Running low?
          </div>
          <div className="mt-1 text-[12.5px]" style={{ color: A.body }}>
            Your next bottle can arrive before this one runs out, at 15% off every order.
          </div>
        </div>
      </div>
    </Block>
  );
}
